import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { LayoutDashboard, Calendar, ChevronDown, Download, Users, Headset, Star, AlertTriangle, PhoneCall, Coins, TrendingUp, ShieldCheck, Truck } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { GET_ORGANISATION_TEAM_STATS, type OrganisationTeamStats } from './teamStats'
import { deriveRosterStatuses } from './rosterStatus'
import { buildRosterCsv, downloadCsv } from './exportCsv'
import KpiTile from './KpiTile'
import ModuleCard from './ModuleCard'
import RosterTable from './RosterTable'

interface OverviewTabProps {
  organisationId: number | null
  organisationName?: string
}

// Module cards have no per-module icon in the data; cycle the mockup's icon set deterministically
const MODULE_ICONS = [PhoneCall, Coins, TrendingUp, ShieldCheck, Truck]

const WINDOW_OPTIONS = [7, 30, 90]

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] tracking-[1.8px] font-semibold" style={{ color: 'var(--m2-muted)' }}>
    {children}
  </div>
)

const signedDelta = (current: number, previous: number, suffix: string, decimals = 0): string | null => {
  if (previous === 0) return null
  const diff = current - previous
  const formatted = decimals > 0 ? Math.abs(diff).toFixed(decimals) : String(Math.abs(diff))
  return `${diff >= 0 ? '↑' : '↓'} ${formatted} ${suffix}`
}

const OverviewTab: React.FC<OverviewTabProps> = ({ organisationId, organisationName }) => {
  const [stats, setStats] = useState<OrganisationTeamStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [windowDays, setWindowDays] = useState(30)
  const now = useMemo(() => Date.now(), [])

  const fetchStats = useCallback(async () => {
    if (!organisationId) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(false)
      const response = await authorizedGraphQLRequest<{ getOrganisationTeamStats: OrganisationTeamStats }>(GET_ORGANISATION_TEAM_STATS, { organisationId, windowDays })
      setStats(response.getOrganisationTeamStats)
    } catch (err) {
      console.error('Failed to fetch organisation team stats:', err)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [organisationId, windowDays])

  const handleExport = useCallback(() => {
    if (!stats) return
    const csv = buildRosterCsv(stats.members, deriveRosterStatuses(stats.members, now), now)
    downloadCsv(csv, `team-overview-${new Date(now).toISOString().split('T')[0]}.csv`)
  }, [stats, now])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const statuses = useMemo(() => (stats ? deriveRosterStatuses(stats.members, now) : new Map()), [stats, now])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Failed to load team stats.</p>
        <button type="button" onClick={fetchStats} className="text-[13px] font-semibold px-4 py-2 rounded-md" style={{ background: 'var(--m2-brand)', color: 'var(--m2-lime)' }}>
          Retry
        </button>
      </div>
    )
  }

  if (!stats || stats.members.length === 0) {
    return (
      <div className="text-center text-gray-500 py-12">
        <p>No team members in this organisation yet.</p>
      </div>
    )
  }

  const { kpis, modules, members } = stats
  const deltaSuffix = kpis.windowDays === 30 ? 'vs last month' : `vs previous ${kpis.windowDays} days`
  const engagementPct = kpis.totalReps > 0 ? Math.round((kpis.activeReps / kpis.totalReps) * 100) : 0
  const attention = members.filter((m) => statuses.get(m.userId) === 'stalled' || statuses.get(m.userId) === 'risk')
  const stalledCount = members.filter((m) => statuses.get(m.userId) === 'stalled').length
  const riskCount = members.filter((m) => statuses.get(m.userId) === 'risk').length
  const totalRolePlays = modules.reduce((sum, m) => sum + m.rolePlayCount, 0)

  return (
    <div className="module2-coach">
      <div
        className="rounded-[14px] overflow-hidden"
        style={{ background: '#FAFAF7', border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(15,76,58,0.04), 0 4px 16px rgba(15,76,58,0.06)' }}
      >
        {/* Topbar */}
        <div className="flex items-center justify-between gap-4 flex-wrap px-6 pt-[18px] pb-4" style={{ background: 'var(--m2-card)', borderBottom: '1px solid #EEF1ED' }}>
          <div className="flex items-center gap-3.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--m2-brand)', color: 'var(--m2-lime)' }}>
              <LayoutDashboard size={19} />
            </div>
            <div>
              <div className="text-[15px] font-bold leading-tight" style={{ color: 'var(--m2-ink)' }}>
                Team dashboard
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: 'var(--m2-muted)' }}>
                Aggregates{organisationName ? ` · ${organisationName}` : ''} &middot; {kpis.totalReps} {kpis.totalReps === 1 ? 'rep' : 'reps'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-[5px] rounded-md border-none cursor-pointer"
                  style={{ background: 'var(--m2-mint)', color: 'var(--m2-ink)' }}
                >
                  <Calendar size={13} />
                  Last {windowDays} days
                  <ChevronDown size={13} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {WINDOW_OPTIONS.map((days) => (
                  <DropdownMenuItem key={days} className="text-[12px]" onSelect={() => setWindowDays(days)}>
                    Last {days} days
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              type="button"
              onClick={handleExport}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border-none cursor-pointer"
              style={{ background: 'var(--m2-brand)', color: 'var(--m2-lime)' }}
            >
              <Download size={13} />
              Export
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="px-6 pt-[18px] pb-2">
          <div className="mb-2">
            <SectionLabel>ACROSS THE TEAM</SectionLabel>
          </div>
          <div className="grid grid-cols-2 min-[800px]:grid-cols-4 gap-2.5 mb-5">
            <KpiTile icon={<Users size={13} />} label="Active reps" value={String(kpis.activeReps)} unit={`/ ${kpis.totalReps}`} delta={`${engagementPct}% engagement`} />
            <KpiTile
              icon={<Headset size={13} />}
              label="Total sessions"
              value={String(kpis.sessionsCurrent)}
              delta={kpis.sessionsPrevious > 0 ? `${kpis.sessionsCurrent - kpis.sessionsPrevious >= 0 ? '+' : ''}${kpis.sessionsCurrent - kpis.sessionsPrevious} ${deltaSuffix}` : null}
            />
            <KpiTile
              icon={<Star size={13} />}
              label="Team avg score"
              value={kpis.avgScoreCurrent.toFixed(1)}
              unit="/ 5.0"
              delta={signedDelta(kpis.avgScoreCurrent, kpis.avgScorePrevious, deltaSuffix, 1)}
              variant="dark"
            />
            <KpiTile
              icon={<AlertTriangle size={13} />}
              label="Needs attention"
              value={String(attention.length)}
              delta={attention.length > 0 ? `${riskCount} at risk · ${stalledCount} stalled` : 'All reps on track'}
              variant="warn"
            />
          </div>
        </div>

        {/* Performance by module */}
        <div className="px-6 pb-2">
          <div className="flex items-center justify-between mb-2.5">
            <SectionLabel>PERFORMANCE BY MODULE</SectionLabel>
            <div className="text-[11px]" style={{ color: 'var(--m2-muted)' }}>
              {modules.length} {modules.length === 1 ? 'module' : 'modules'} &middot; {totalRolePlays} role-plays
            </div>
          </div>
          {modules.length === 0 ? (
            <div className="text-center text-[12px] py-6 rounded-[10px]" style={{ color: 'var(--m2-muted)', background: 'var(--m2-card)', border: '1px solid var(--m2-line)' }}>
              No modules subscribed for this organisation.
            </div>
          ) : (
            <div className="grid grid-cols-1 min-[800px]:grid-cols-2 gap-2.5 mb-2">
              {modules.map((mod, i) => {
                const Icon = MODULE_ICONS[i % MODULE_ICONS.length]
                return <ModuleCard key={mod.moduleId} module={mod} icon={<Icon size={15} />} />
              })}
            </div>
          )}
        </div>

        {/* Team roster */}
        <div className="px-6 pt-3.5 pb-[22px]">
          <div className="mb-2.5">
            <SectionLabel>TEAM ROSTER</SectionLabel>
          </div>
          <RosterTable members={members} statuses={statuses} now={now} />
        </div>
      </div>
    </div>
  )
}

export default OverviewTab
